'use client';

import { AskQuestionForm } from '@/components/ask-question-form';
import { BackButton } from '@/components/back-button';
import { buttonVariants } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { ArrowLeft } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

export default function AskQuestion() {
  const router = useRouter();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const shouldBlockNavigationRef = useRef(false);
  const hasPushedStateRef = useRef(false);
  const pendingNavigationRef = useRef<string | null>(null);

  const handleBackClick = () => {
    if (isFormDirty) {
      pendingNavigationRef.current = null; // null means go back
      setShowWarningDialog(true);
    } else {
      router.back();
    }
  };

  const handleConfirmLeave = () => {
    setShowWarningDialog(false);
    shouldBlockNavigationRef.current = true;

    if (pendingNavigationRef.current) {
      // Navigate to the pending URL (from link click)
      router.push(pendingNavigationRef.current);
      pendingNavigationRef.current = null;
    } else {
      // Go back (from back button or browser back)
      router.back();
      router.back();
    }
  };

  const handleCancelLeave = () => {
    setShowWarningDialog(false);
    // We're already on the page since we pushed state back, so no action needed
  };

  // Handle browser back/forward button
  useEffect(() => {
    if (!isFormDirty) {
      hasPushedStateRef.current = false;
      return;
    }

    const handlePopState = () => {
      if (shouldBlockNavigationRef.current) {
        // Navigation was confirmed, allow it to proceed
        shouldBlockNavigationRef.current = false;
        hasPushedStateRef.current = false;
        return;
      }
      // Prevent the navigation by pushing the current state back
      // This cancels the back navigation
      window.history.pushState(null, '', window.location.href);
      setShowWarningDialog(true);
    };

    // Push a state entry when form becomes dirty so we can intercept back navigation
    if (!hasPushedStateRef.current) {
      window.history.pushState(null, '', window.location.href);
      hasPushedStateRef.current = true;
    }

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isFormDirty]);

  // Handle browser close/refresh (beforeunload)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isFormDirty) {
        event.preventDefault();
        // Modern browsers ignore custom messages, but we still need to call preventDefault
        event.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isFormDirty]);

  // Handle link clicks (navbar, etc.)
  useEffect(() => {
    if (!isFormDirty) return;

    const handleLinkClick = (event: MouseEvent) => {
      // Find the closest anchor element
      const target = event.target as HTMLElement;
      const anchor = target.closest('a');

      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Skip external links, hash links, and same-page links
      if (
        href.startsWith('http') ||
        href.startsWith('#') ||
        href === window.location.pathname
      ) {
        return;
      }

      // Prevent navigation and show warning
      event.preventDefault();
      event.stopPropagation();
      pendingNavigationRef.current = href;
      setShowWarningDialog(true);
    };

    // Use capture phase to intercept before Next.js handles the click
    document.addEventListener('click', handleLinkClick, true);

    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [isFormDirty]);

  return (
    <section className="flex flex-col gap-4 p-8 max-w-4xl w-full mx-auto">
      <div className="flex items-start gap-1 flex-col">
        <div className="flex items-center gap-2 ">
          <BackButton
            className={cn(
              buttonVariants({ variant: 'ghost', size: 'icon' }),
              'text-[#24292E] hover:bg-ihs-blue/20'
            )}
            onBackClick={handleBackClick}
          >
            <ArrowLeft />
          </BackButton>
          <h1 className="text-base font-bold text-[#24292E]">Add a question</h1>
        </div>

        <h2 className="text-sm text-gray-500 ml-2">
          Required fields are marked with{' '}
          <span className="text-red-500">*</span> - all other fields are
          optional.
        </h2>
      </div>

      <AskQuestionForm onFormDirtyChange={setIsFormDirty} />

      <AlertDialog
        open={showWarningDialog}
        onOpenChange={(open) => {
          if (!open) {
            // Dialog was closed (e.g., clicking outside or pressing escape)
            handleCancelLeave();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-bold">
              Leave without saving?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your
              progress will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex justify-between">
            <AlertDialogCancel
              className={cn(
                buttonVariants({ variant: 'outline', size: 'lg' }),
                'uppercase font-bold flex-1 w-full text-black hover:bg-black/10 hover:text-black border-black'
              )}
              onClick={handleCancelLeave}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(
                buttonVariants({ variant: 'destructive', size: 'lg' }),
                'uppercase font-bold flex-1 w-full'
              )}
              onClick={handleConfirmLeave}
            >
              Leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
